/**
 * 邮百工GUI统一渲染逻辑 - 统一内容渲染器
 * 根据内容类型智能选择渲染组件
 */

import React, { useState, useEffect } from 'react';
import { processContent } from './ContentUtils';
import LazyMermaidRenderer from '../LazyMermaidRenderer';
import EnhancedMarkdown from '../enhanced/EnhancedMarkdown';
import { detectContentType } from './ContentUtils';

/**
 * 统一内容渲染器
 * 根据内容类型智能选择合适的渲染组件，支持混合内容类型
 * 
 * @param {Object} props - 组件属性
 * @param {string} props.content - 要渲染的内容
 * @param {string} props.className - 自定义CSS类名
 * @param {boolean} props.collapsible - 是否启用内容折叠功能
 * @param {number} props.collapseThreshold - 折叠阈值，超过多少行开始折叠
 * @param {Function} props.onContentClick - 内容点击回调函数
 * @param {Object} props.renderOptions - 额外的渲染选项
 * @param {boolean} props.showCodeBlockHeader - 是否显示代码块标题
 * @returns {JSX.Element} - 渲染后的内容组件
 */
const UnifiedContentRenderer = ({
  content,
  className = '',
  collapsible = false,
  collapseThreshold = 15,
  onContentClick = null,
  renderOptions = {},
  showCodeBlockHeader = true,
  onA2UIAction = null, // 🆕 A2UI动作回调
  messageId = null,    // 🆕 消息ID作为SurfaceID
  isStreaming = false,
}) => {
  // 所有hooks必须在组件顶层调用，不能受条件影响
  // const [showDebug, setShowDebug] = useState(false); // 移除调试状态
  
  // 检测内容类型
  const { primaryType, types } = detectContentType(content);
  
  // 渲染内容
  const renderContent = () => {
    // 如果内容为空，直接返回null
    if (!content || content.trim() === '') {
      return null;
    }
    
    // 根据主要内容类型选择合适的渲染组件
    switch (primaryType) {
      case 'mermaid':
        if (isStreaming) {
          return (
            <div
              className="mermaid-streaming-placeholder"
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                minHeight: 76,
                padding: '14px 16px',
                color: '#aebbd0',
                fontSize: 13,
                border: '1px dashed rgba(110, 231, 183, 0.32)',
                borderRadius: 10,
                background: 'rgba(110, 231, 183, 0.06)',
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  flex: '0 0 auto',
                  borderRadius: '50%',
                  background: '#6ee7b7',
                  boxShadow: '0 0 0 4px rgba(110, 231, 183, 0.13)',
                }}
              />
              正在生成图表，将在图表完整后展示
            </div>
          );
        }
        return (
          <LazyMermaidRenderer
            chart={content}
            theme={renderOptions.theme || 'dark'}
            showDebugInfo={renderOptions.showDebugInfo || false}
            {...(renderOptions.mermaidOptions || {})}
          />
        );
        
      case 'markdown':
      default:
        return (
          <EnhancedMarkdown
            content={content}
            types={types}
            collapsible={collapsible}
            collapseThreshold={collapseThreshold}
            onContentClick={onContentClick}
            customClass={renderOptions.customClass}
            tableClass={renderOptions.tableClass}
            codeClass={renderOptions.codeClass}
            inlineMode={renderOptions.inlineMode || false}
            nestingLevel={renderOptions.nestingLevel || 0}
            compactMode={renderOptions.compactMode || true} // 默认启用紧凑模式
            showCodeBlockHeader={showCodeBlockHeader}
            onA2UIAction={onA2UIAction} // 🆕 传递A2UI动作回调
            messageId={messageId}       // 🆕 传递消息ID
            isStreaming={isStreaming}
          />
        );
    }
  };
  
  // 最终渲染
  return (
    <div className={`unified-content-container ${className}`} style={{ 
      margin: 0, 
      padding: 0,
      lineHeight: 1.3
    }}>
      {/* 调试器切换按钮（开发环境）- 已移除 */}
      {/* {process.env.NODE_ENV === 'development' && !inlineMode && (
        <div className="content-debug-toggle" onClick={() => setShowDebug(!showDebug)}>
          {showDebug ? '隐藏类型信息' : '显示类型信息'}
        </div>
      )} */}
      
      {/* 内容类型调试信息 - 已移除 */}
      {/* {showDebug && (
        <div className="content-debug-info">
          <div><strong>主要类型:</strong> {primaryType}</div>
          <div><strong>所有类型:</strong> {types.join(', ')}</div>
        </div>
      )} */}
      
      {/* 实际内容渲染 */}
      {renderContent()}
      
      {/* 样式 */}
      <style jsx>{`
        .unified-content-container {
          width: 100%;
          position: relative;
        }
        
        .unified-content-container.inline-content {
          display: inline;
          width: auto;
        }
        
        // 移除调试相关样式
        /* .content-debug-toggle { ... } */
        /* .content-debug-toggle:hover { ... } */
        /* .content-debug-info { ... } */
      `}</style>
    </div>
  );
};

export default UnifiedContentRenderer; 